import React, { useState, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { AuthContext } from '../../contexts/AuthContext';
import { Loading } from '../../components/utility/Utility';

export default function ConfirmSignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleConfirmSignUp, handleResendSignUpCode } = useContext(AuthContext);

  // Fallback for email if the user refreshes the page
  const [email, setEmail] = useState(location.state?.email || '');
  const [formErrors, setFormErrors] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleResendCode = async () => {
    if (!email) {
      setResendMessage('Email address is not available. Please go back to sign up.');
      return;
    }
    if (resendDisabled) {
      return;
    }
    
    try {
      await handleResendSignUpCode(email);
      setResendMessage('A new confirmation code has been sent to your email.');
      
      // Disable button and start countdown
      setResendDisabled(true);
      setCountdown(60);
      
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Countdown timer
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      setResendMessage(error.message || 'Failed to resend code.');
      setResendDisabled(false);
      setCountdown(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  if (!email && !location.state?.email) {
     // If email is not available in state or location, prompt user to enter it.
     // This is a recovery mechanism.
    return (
        <div className="signup">
            <h1 className='signup__header'>Confirm Sign Up</h1>
            <hr className="signup__horizontal-rule" />
            <p className="signup__form-description">Please provide the email you used to sign up.</p>
            <Formik
                initialValues={{ email: '' }}
                validationSchema={Yup.object({ email: Yup.string().email('Invalid email address').required('Required')})}
                onSubmit={(values) => setEmail(values.email)}
            >
                <Form className="auth-form signup__form">
                    <Field name="email" type="email" placeholder="Your email address" />
                    <ErrorMessage component="div" className="form-error" name="email" />
                    <button type="submit" className="gradient">Continue</button>
                </Form>
            </Formik>
        </div>
    )
  }

  return (
    <div className="signup">
      <h1 className='signup__header'>Confirm Your Account</h1>
      <hr className="signup__horizontal-rule" />
      <p className="signup__form-description">A confirmation code has been sent to <strong>{email}</strong>. Please enter it below to verify your account.</p>
      <Formik
        initialValues={{ confirmationCode: '' }}
        validationSchema={Yup.object({
          confirmationCode: Yup.string().required('Confirmation code is required.'),
        })}
        onSubmit={async (values) => {
          setSubmitting(true);
          try {
            await handleConfirmSignUp(email, values.confirmationCode);
            navigate('/login', { state: { message: 'Your account has been confirmed! Please log in.' } });
          } catch (err) {
            setFormErrors(err.message || 'An unexpected error occurred.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <Form className="auth-form signup__form">
          {/* <label className="signup__form-label" htmlFor="confirmationCode">Confirmation Code</label> */}
          <Field name="confirmationCode" type="text" />
          <ErrorMessage component="div" className="form-error" name="confirmationCode" />

          {formErrors && <div className="form-errors">{formErrors}</div>}
          {submitting && <Loading className="signup__loading" />}
          {!submitting && <button type="submit" className="gradient">Confirm Account</button>}
        </Form>
      </Formik>
      <div className="resend-code">
        <button 
          onClick={handleResendCode} 
          className="link-button" 
          disabled={resendDisabled}
          style={{ 
            width: 'fit-content',
            opacity: resendDisabled ? 0.5 : 1,
            cursor: resendDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          {resendDisabled ? `Resend Code (${countdown}s)` : 'Resend Code'}
        </button>
        {resendMessage && <p className="signup__form-description">{resendMessage}</p>}
      </div>
    </div>
  );
} 